import SockJS from 'sockjs-client';

import isTest from './helpers/isTest';
import globalObject from './helpers/globalObject';
import performanceObject from './helpers/performanceObject';

const options = {
  transports: [
    'websocket',
    'xdr-streaming',
    'xhr-streaming',
    'iframe-eventsource',
    'xdr-polling',
    'xhr-polling',
  ],
};

let verboseLogging;
let DO_NOT_CONNECT = false;
if ('localStorage' in globalObject) {
  if (globalObject.localStorage.getItem('sockjs-transports')) {
    options.transports = JSON.parse(globalObject.localStorage.getItem('sockjs-transports'));
  }

  verboseLogging = Boolean(globalObject.localStorage.getItem('websocket-service:verbose-logging'));

  /**
   * Sometimes it's handy to turn off sockets when testing someting
   * on a live org on production that has a lot of traffic
   */
  if (globalObject.localStorage.getItem('sockjs-do-not-connect')) {
    DO_NOT_CONNECT = true;
  }
}

const MAX_RECONNECTION_DELAY = 25_000; // 25s

export default class WebSocketService {
  constructor(url, params = {}) {
    this.url = url;

    this.onOpenCallbacks = [];
    this.onReconnectCallbacks = [];
    this.onMessageCallbacks = [];
    this.onErrorCallbacks = [];
    this.onCloseCallbacks = [];
    this.connectionAttempts = 0;
    this.reconnectTimer = null;
    this.reconnecting = false;
    // mobile client
    this.shouldStayClosed = false;
    this.immediateReconnects = params && params.immediateReconnects;

    if (DO_NOT_CONNECT) {
      return;
    }

    this._connect();

    if (!isTest) {
      this._watchHeartbeats();
    }
  }

  _watchHeartbeats() {
    const loop = () => {
      if (this._lastHeartbeat && !this.reconnecting) {
        const now = performanceObject.now();
        const secondsPassed = (now - this._lastHeartbeat) / 1000;
        if (secondsPassed > 60) {
          console.warn(`No heartbeat within: ${secondsPassed} seconds. Reconnecting...`);
          this.reconnect();
        } else if (verboseLogging) {
          console.info(`${secondsPassed} since last heartbeat. All good.`);
        }
      }

      setTimeout(loop, 5000);
    };

    loop();
  }

  _connect() {
    if (isTest) {
      return;
    }

    const self = this;
    this.socket = new SockJS(this.url, undefined, options);
    this.socket.onopen = function onopen(event) {
      self._onOpenHandler(event);
      self.reconnecting = false;
    };

    this.socket.onclose = this._onCloseHandler.bind(this);
    this.socket.onerror = this._onErrorHandler.bind(this);
    this.socket.onmessage = this._onMessageHandler.bind(this);
  }

  _onOpenHandler(event) {
    this.onOpenCallbacks.forEach((cb) => {
      cb(event);
    });

    if (this.connectionAttempts > 0) {
      const cA = this.connectionAttempts;
      this.onReconnectCallbacks.forEach((cb) => {
        cb(event, cA);
      });
    }

    this.connectionAttempts = 0;
    this._lastHeartbeat = performanceObject.now();
  }

  _onCloseHandler(event) {
    if (!isTest) {
      console.log('_onCloseHandler', event);
    }

    this.onCloseCallbacks.forEach((cb) => {
      cb(event);
    });

    if (this.shouldStayClosed) {
      return;
    }

    this.reconnecting = false;
    this.reconnect();
  }

  _onErrorHandler(event) {
    console.log('_onErrorHandler', event);

    this.onErrorCallbacks.forEach((cb) => {
      cb(event);
    });

    this.reconnecting = false;
    this.reconnect();
  }

  _onHeartbeatHandler() {
    this._lastHeartbeat = performanceObject.now();
  }

  _onMessageHandler(message) {
    try {
      const data = JSON.parse(message.data);

      if (data.heartbeat) {
        this._onHeartbeatHandler(message);
      } else {
        this.onMessageCallbacks.forEach((cb) => {
          cb(data);
        });
      }
    } catch (e) {
      console.log('invalid data, we expect valid json!', message.data, e);
    }
  }

  // https://en.wikipedia.org/wiki/Exponential_backoff
  // this will return 2s, 4s, 8s, 16s etc. with max limit - MAX_RECONENCTION_DELAY
  // to exponentially delay consecutive reconnection attempts
  _getBackoffDelay(attempt) {
    return Math.min(2 ** attempt * 1000, MAX_RECONNECTION_DELAY);
  }

  // Public
  onOpen(cb) {
    this.onOpenCallbacks.push(cb);
    return this;
  }

  onReconnect(cb) {
    this.onReconnectCallbacks.push(cb);
    return this;
  }

  offReconnect(cb) {
    const index = this.onReconnectCallbacks.indexOf(cb);
    if (index >= 0) {
      this.onReconnectCallbacks.splice(cb, 1);
    }
    return this;
  }

  onClose(cb) {
    this.onCloseCallbacks.push(cb);
    return this;
  }

  onError(cb) {
    this.onErrorCallbacks.push(cb);
    return this;
  }

  onMessage(cb) {
    this.onMessageCallbacks.push(cb);
    return this;
  }

  removeListener(type, cb) {
    const cbArray = this[`${type}Callbacks`];

    const index = cbArray ? cbArray.findIndex((item) => item === cb) : -1;
    if (index >= 0) {
      cbArray.splice(index, 1);
    }
  }

  send(data) {
    if (!this.socket) {
      return;
    }

    if (this.socket.readyState === 1) {
      this.socket.send(JSON.stringify(data));
    } else if (this.socket.readyState === 0) {
      // not yet opened
      this.onOpen(() => {
        this.send(data);
      });
    }
  }

  close(shouldStayClosed = false) {
    this.shouldStayClosed = shouldStayClosed;
    this.socket.close();
    return this;
  }

  reconnect(forceImmediateReconnection = false) {
    this.shouldStayClosed = false;
    if (this.reconnecting && !forceImmediateReconnection) {
      return false;
    }

    this.reconnecting = true;
    this.close();

    clearTimeout(this.reconnectTimer);

    if (forceImmediateReconnection || this.immediateReconnects) {
      this._connect();
      return this;
    }

    const delay = this._getBackoffDelay(++this.connectionAttempts);

    if (!isTest) {
      console.log('going to retry after delay: ', delay);
    }

    this.reconnectTimer = setTimeout(this._connect.bind(this), delay);

    return this;
  }
}
